import fullpage from '../lib/fullpage/fullpage.extensions.min'

import { hideInfoSection } from './infoSection'
import businessBook from './businessBook'
import { initProjectItemsNavigation, scrollActiveDotIntoView } from './projectItem'

const loadedImageIndexes = []
const loadedVideoIndexes = []
const GALLERY_IMAGE_WINDOW = 5
let galleryActiveIndex = 0
let gallerySlideCount = 0
let sidrFocusCleanup = null
const startedVideoIndexes = []
const savedVideoTimes = {}
let volumeListener
let currentSlide = 0
let userMuted = localStorage.getItem('userMuted') !== 'false'
let scrollHintObserver = null
let videoPreloadTimer = null
const isMobileVideoMode = window.matchMedia('(max-width: 767px)').matches || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
const MOBILE_MIN_BUFFER_AHEAD = 2.0
let videoProgressWatchdog = null
let videoSectionActive = false
let infoExitInProgress = false
let nativeInfoScrollCleanup = []
let nativeInfoEntryCleanup = null
let nativeInfoEntryLocked = false
let nativeInfoEntryReleaseTimer = null
let nativeInfoEntryPinRaf = null
let videoLoaderPositionRaf = null
const slideInfoTimers = {}
const isIOSVideoDevice = /iPhone|iPad|iPod/i.test(navigator.userAgent)

const HOME_IMAGE_DURATION_MS = 12000
const HOME_MEDIA_FADE_MS = 2800
const HOME_SEEN_STORAGE_KEY = '3veHomeCarouselSeenIdsV1'
let homeCarouselItems = []
let homeCarouselIndex = 0
let homeCarouselTimer = null
let homeCarouselToken = 0
let homeCarouselInitialized = false
let homeCarouselPaused = false
let homeCarouselCurrentType = null
let homeCarouselVideoEndedHandler = null
let homeCarouselFullPage = null
let homeActiveImageLayerIndex = 0

export const initFullPage = (page) => {
  const nativeInfoScrollPages = [
    'infographics',
    '_3d_animations',
    'virtual_tours',
    'virtual_reality'
  ]

  const useNativeInfoScroll = nativeInfoScrollPages.includes(page)

  listenToSectionButtonsClick()

  loadedImageIndexes.length = 0
  loadedVideoIndexes.length = 0
  startedVideoIndexes.length = 0
  Object.keys(savedVideoTimes).forEach(key => delete savedVideoTimes[key])

  // Fullpage doesn't allow initializing twice
  if (window.fullpage_api)
    window.fullpage_api.destroy('all')

  // noinspection JSPotentiallyInvalidConstructorUsage
  const fullPage = new fullpage('#fullpage', {
    scrollBar: false,
    scrollOverflow: !useNativeInfoScroll,
    scrolloverflowmacstyle: false,
    scrollOverflowReset: false,
    slideSelector: '.horizontal-scrolling',
    v2compatible: false,
    normalScrollElements: useNativeInfoScroll
      ? '#sidr, .selectable-container, .section.scroll'
      : '#sidr, .selectable-container',
    controlArrows: false,
    fadingEffect: 'slides',
    slidesNavigation: true,
    slidesNavPosition: 'bottom',
    navigationTooltips: ['firstSlide'],
    showActiveTooltip: true,
    loopHorizontal: true,
    menu: '#sidr',
    afterRender: function (_origin, destination, _direction) {
      const index = destination?.index
      const contentContainer =
        document.getElementById(index === undefined ? 'container-first-image' : `slide-index-${index}`)

      const isImage = !!contentContainer.dataset.image
      const isVideo = contentContainer.dataset.video === "true"
      videoSectionActive = isVideo

      if (!isVideo)
        hideVideoLoading(index)
      const isIframe = contentContainer.dataset.iframe === "true"
      const isQuotePage = page === 'quote'

      // V9.12 HOME: la primera pantalla tiene su propio carrusel multimedia.
      // Se inicializa una sola vez y no usa la canalización de slides horizontales.
      if (page === 'index' && index === undefined) {
        initHomeHeroCarousel(fullPage)
        showSlideInfo(0)
        return
      }

      const isProjectItem =
        page === 'infographics' || page === 'virtual_reality' || page === 'virtual_tours' || page === '_3d_animations'

      if (isProjectItem) {
        initProjectItemsNavigation()
        arrangeTagButtons()
        window.setTimeout(() => updateProjectImageWindow(index === undefined ? 0 : index), 0)
      }

      if (isIframe)
        initTourInteractionHints()

      if (isImage)
        showImage(contentContainer, contentContainer, index)

      else if (isVideo) {
        const video = document.getElementById(`video-${index === undefined ? 0 : index}`)
        currentSlide = index || 0
        showVideoLoading(currentSlide)
        // HOME usa exactamente la misma canalización de reproducción
        // que Animaciones 3D / Realidad Virtual.
        if (startedVideoIndexes.includes(currentSlide))
          resumeVideoSafely(video, currentSlide)
        else
          startVideoSafely(video, currentSlide, true)

        showVolume(currentSlide, video)
        disableScroll()
      }

      else if (isIframe) {
        // El iframe ya utiliza exactamente la misma geometría de Tours.
        // Sólo se inicializa la ayuda visual de interacción.
        initTourInteractionHints()
        disableScroll()
      }

      if (isQuotePage) {
        const sideMenu = document.getElementById('side-menu')
        const logoContainer = document.getElementById('logo-container')

        sideMenu.style.color = '#222222'
        
        sideMenu.style.filter = 'none'
        logoContainer.classList.add('container-hidden')
      }

      showSlideInfo(index)
    },
    afterLoad: function (origin, destination, _direction) {    // sections ('up' and 'down')
      if (!fullPage)
        return

      const index = destination.index

      // HOME no utiliza slide-index-0: su primera pantalla es
      // #container-first-image. Al volver desde clientes necesitamos leer
      // data-video/data-iframe de ese contenedor real para reanudar el medio.
      const contentContainer =
        page === 'index' && index === 0
          ? document.getElementById('container-first-image')
          : document.getElementById(index === undefined ? 'container-first-image' : `slide-index-${index}`)

      const sideMenu = document.getElementById('side-menu')
      const logoContainer = document.getElementById('logo-container')
      const previousButton = document.getElementById('btn-previous')
      const nextButton = document.getElementById('btn-next')

      const isQuotePage = page === 'quote'
      const isContent = index === 0 && !isQuotePage
      const isInfoSection = index > 0 && !isQuotePage
      const isNativeInfoSection = useNativeInfoScroll && isInfoSection
      const isComingFromInfoSection = origin.index > 0 && !isQuotePage
      const isHomeBookSection = index === 1 && page === 'index'
      const isVideo = contentContainer && contentContainer.dataset.video === "true"
      const isIframe = contentContainer && contentContainer.dataset.iframe === "true"

      if (isContent) {
        if (page === 'index')
          resumeHomeHeroCarousel()

        restoreVideosAfterIOSInfoSection()
        videoSectionActive = isVideo
        if (!videoSectionActive)
          hideVideoLoading(currentSlide)
        enableScroll()
        
      // En la primera sección, fullPage vuelve a controlar el desplazamiento vertical.
      fullPage.setAllowScrolling(true, 'up,down')

      document.body.classList.remove('info-scroll-active')
sideMenu.style.color = '#FFFFFF'
        
        sideMenu.style.filter = 'drop-shadow(1px 1px 0 rgba(0,0,0,0.9))'
        logoContainer.classList.remove('container-hidden')
        previousButton.classList.add('container-hidden')
        nextButton.classList.remove('container-hidden')

        if (isComingFromInfoSection && isVideo && page !== 'index') {
          const video = document.getElementById(`video-${currentSlide}`)
          resumeVideoSafely(video, currentSlide)
        }

        if (page === 'index' && isComingFromInfoSection)
          resumeHomeHeroCarousel()
      }
      else if (isInfoSection) {
        videoSectionActive = false
        stopVideoProgressWatchdog()
        hideVideoLoading(currentSlide)
        hideVideosForIOSInfoSection()
        enableScroll()
        
      // Solo las páginas informativas largas (infografías, animaciones,
      // tours y VR) ceden el scroll vertical al navegador.
      // La segunda sección de HOME debe seguir controlada por fullPage
      // para que la rueda/touch permita volver a la primera sección.
      fullPage.setAllowScrolling(!isNativeInfoSection, 'up,down')

      if (isNativeInfoSection)
        document.body.classList.add('info-scroll-active')
      else
        document.body.classList.remove('info-scroll-active')
sideMenu.style.color = '#222222'
        
        sideMenu.style.filter = 'none'
        logoContainer.classList.add('container-hidden')
        previousButton.classList.remove('container-hidden')
        nextButton.classList.add('container-hidden')

        if (isHomeBookSection)
          businessBook.isPresent() && businessBook.animateContent()
        else {
          showInfoSection(index)
          showScrollHint()
        }
      }
    },
    afterSlideLoad: function (_section, _origin, destination, _direction) {
      const index = destination?.index
      currentSlide = index

      scrollActiveDotIntoView(index)

      if (page === 'infographics' || page === 'virtual_reality' || page === 'virtual_tours' || page === '_3d_animations')
        updateProjectImageWindow(index)

      const contentContainer = document.getElementById(index === undefined ? 'container-first-image' : `slide-index-${index}`)
      const isImage = !!contentContainer.dataset.image
      const isVideo = contentContainer.dataset.video === "true"
      videoSectionActive = isVideo

      if (!isVideo)
        hideVideoLoading(index)

      if (isImage) {
        const imageContainer = document.querySelector(`.image-${index}`)
        showImage(contentContainer, imageContainer, index)
      }
      else if (isVideo) {
        const video = document.getElementById(`video-${index}`)
        showVideoLoading(index)
        bindVideoAutoAdvance(video, index, page)

        if (startedVideoIndexes.includes(index))
          resumeVideoSafely(video, index)
        else
          startVideoSafely(video, index, true)

        showVolume(index, video)
        showSlideInfo(index, 325)
      }
      else {
        // Iframes y otros contenidos: aparición rápida y consistente.
        showSlideInfo(index, 325)

        if (contentContainer.dataset.iframe === "true")
          initTourInteractionHints()
      }
    },
    onSlideLeave (section, origin, destination) {
      removeVolumeListener()
      cancelVideoPreloadTimer()
      stopVideoProgressWatchdog()

      const originInfoIndex = origin?.index
      hideSlideInfo(originInfoIndex)

      const destinationIndex = destination?.index
      const destinationContainer = destinationIndex === undefined
        ? null
        : document.getElementById(`slide-index-${destinationIndex}`)

      videoSectionActive = destinationContainer?.dataset.video === 'true'

      if (videoSectionActive)
        showVideoLoading(destinationIndex)
      else
        hideVideoLoading(destinationIndex)

      const originIndex = origin?.index
      const video = document.getElementById(`video-${originIndex}`)

      if (video)
        unloadVideoSource(video, originIndex)
    },
    onLeave: function (origin, destination, _direction) {
      hideScrollHint()

      if (destination.index > 0) {
        videoSectionActive = false
        stopVideoProgressWatchdog()
      }

      hideVideoLoading(currentSlide)

      // HOME: al abandonar la portada se pausa el carrusel completo.
      // Vídeo conserva currentTime; imagen conserva el item actual; Tour permanece cargado.
      if (page === 'index' && origin.index === 0 && destination.index > 0) {
        pauseHomeHeroCarousel()
        hideVideoLoading(0)
      }

      const isSection = document.getElementById(`info-section-${origin.index - 1}`)
      const isInfoSection = isSection && origin.index > 0

      if (isInfoSection)
        hideInfoSection(origin.index)
    },
  })

  window.fullpage_api = fullPage
  bindAllVideoAutoAdvance(page)
  

  
function ensureNativeInfoScrollStyles() {
  if (document.getElementById('3ve-info-scroll-fix'))
    return

  const style = document.createElement('style')
  style.id = '3ve-info-scroll-fix'
  style.textContent = `
    .section.scroll {
      display: block !important;
      height: 100vh !important;
      max-height: 100vh !important;
      overflow-y: auto !important;
      overflow-x: hidden !important;
      box-sizing: border-box !important;
      -webkit-overflow-scrolling: touch;
      overscroll-behavior-y: contain;
    }
  `
  document.head.appendChild(style)
}

ensureNativeInfoScrollStyles()
setupNativeInfoScrollExit(fullPage)
setupNativeInfoScrollEntryLock(fullPage, useNativeInfoScroll)
setupSidrScrollFocus(fullPage, useNativeInfoScroll)



function hideVideosForIOSInfoSection() {
  if (!isIOSVideoDevice)
    return

  document.querySelectorAll('video[id^="video-"]').forEach(video => {
    if (video.dataset.iosPreviousVisibility === undefined)
      video.dataset.iosPreviousVisibility = video.style.visibility || ''

    if (video.dataset.iosPreviousOpacity === undefined)
      video.dataset.iosPreviousOpacity = video.style.opacity || ''

    video.pause()
    video.style.visibility = 'hidden'
    video.style.opacity = '0'
    video.style.pointerEvents = 'none'
  })
}

function restoreVideosAfterIOSInfoSection() {
  if (!isIOSVideoDevice)
    return

  document.querySelectorAll('video[id^="video-"]').forEach(video => {
    video.style.visibility = video.dataset.iosPreviousVisibility || ''
    video.style.opacity = video.dataset.iosPreviousOpacity || ''
    video.style.pointerEvents = ''

    delete video.dataset.iosPreviousVisibility
    delete video.dataset.iosPreviousOpacity
  })
}

function setupNativeInfoScrollEntryLock(fullPage, enabled) {
  if (nativeInfoEntryCleanup) {
    nativeInfoEntryCleanup()
    nativeInfoEntryCleanup = null
  }

  if (!enabled)
    return

  const hero = document.getElementById('container-first-image')
  const scrollSection = document.querySelector('.section.scroll')

  if (!hero || !scrollSection)
    return

  const TRANSITION_LOCK_MS = 950

  const heroIsActive = () => {
    const active = document.querySelector('#fullpage > .section.active') || document.querySelector('.section.active')
    return hero.classList.contains('active') || active === hero
  }

  const forceFirstScrollTop = () => {
    scrollSection.scrollTop = 0
  }

  const stopPinning = () => {
    if (nativeInfoEntryPinRaf) {
      cancelAnimationFrame(nativeInfoEntryPinRaf)
      nativeInfoEntryPinRaf = null
    }
  }

  const keepPinned = () => {
    if (!nativeInfoEntryLocked)
      return

    forceFirstScrollTop()
    nativeInfoEntryPinRaf = requestAnimationFrame(keepPinned)
  }

  const unlock = () => {
    nativeInfoEntryLocked = false
    forceFirstScrollTop()
    stopPinning()
  }

  const onWheel = event => {
    // El menú lateral manda siempre cuando el puntero está encima.
    // Evita que el guard de entrada al scroll nativo robe la rueda al Sidr.
    if (event.target && event.target.closest && event.target.closest('#sidr'))
      return

    // Mientras fullPage anima portada -> scroll, consumimos toda la rueda.
    // Así un gesto largo no se transmite al scroll nativo al terminar la transición.
    if (nativeInfoEntryLocked) {
      event.preventDefault()
      event.stopImmediatePropagation()
      forceFirstScrollTop()
      return
    }

    if (!heroIsActive() || event.deltaY <= 2)
      return

    event.preventDefault()
    event.stopImmediatePropagation()

    nativeInfoEntryLocked = true
    forceFirstScrollTop()
    keepPinned()

    fullPage.moveSectionDown()

    if (nativeInfoEntryReleaseTimer)
      clearTimeout(nativeInfoEntryReleaseTimer)

    nativeInfoEntryReleaseTimer = window.setTimeout(() => {
      nativeInfoEntryReleaseTimer = null
      unlock()
    }, TRANSITION_LOCK_MS)
  }

  window.addEventListener('wheel', onWheel, { capture: true, passive: false })

  nativeInfoEntryCleanup = () => {
    window.removeEventListener('wheel', onWheel, { capture: true })

    if (nativeInfoEntryReleaseTimer) {
      clearTimeout(nativeInfoEntryReleaseTimer)
      nativeInfoEntryReleaseTimer = null
    }

    nativeInfoEntryLocked = false
    stopPinning()
  }
}

function setupNativeInfoScrollExit(fullPage) {
  nativeInfoScrollCleanup.forEach(cleanup => cleanup())
  nativeInfoScrollCleanup = []

  const sections = document.querySelectorAll('.section.scroll')

  sections.forEach(section => {
    let touchStartY = null

    const exitToContent = () => {
      if (infoExitInProgress)
        return

      infoExitInProgress = true

      restoreVideosAfterIOSInfoSection()
      fullPage.setAllowScrolling(true, 'up,down')
      fullPage.moveSectionUp()

      window.setTimeout(() => {
        infoExitInProgress = false
      }, 800)
    }

    const onWheel = event => {
      if (section.scrollTop <= 1 && event.deltaY < -2) {
        event.preventDefault()
        exitToContent()
      }
    }

    const onTouchStart = event => {
      touchStartY = event.touches && event.touches.length
        ? event.touches[0].clientY
        : null
    }

    const onTouchEnd = event => {
      if (touchStartY === null)
        return

      const touch = event.changedTouches && event.changedTouches.length
        ? event.changedTouches[0]
        : null

      if (!touch) {
        touchStartY = null
        return
      }

      const fingerMovedDown = touch.clientY - touchStartY

      if (section.scrollTop <= 1 && fingerMovedDown > 45)
        exitToContent()

      touchStartY = null
    }

    section.addEventListener('wheel', onWheel, { passive: false })
    section.addEventListener('touchstart', onTouchStart, { passive: true })
    section.addEventListener('touchend', onTouchEnd, { passive: true })

    nativeInfoScrollCleanup.push(() => {
      section.removeEventListener('wheel', onWheel)
      section.removeEventListener('touchstart', onTouchStart)
      section.removeEventListener('touchend', onTouchEnd)
    })
  })
}

function listenToSectionButtonsClick() {
    document.getElementById('btn-previous').onclick = () => fullPage.moveSectionUp()
    document.getElementById('btn-next').onclick = () => fullPage.moveSectionDown()
  }
}

function getBufferedAhead(video) {
  if (!video || !video.buffered || video.buffered.length === 0)
    return 0

  const t = video.currentTime

  for (let i = 0; i < video.buffered.length; i++) {
    const start = video.buffered.start(i)
    const end = video.buffered.end(i)

    if (t >= start && t <= end)
      return Math.max(0, end - t)
  }

  return 0
}

function stopVideoProgressWatchdog() {
  if (videoProgressWatchdog) {
    clearInterval(videoProgressWatchdog)
    videoProgressWatchdog = null
  }
}

function startVideoProgressWatchdog(video, index) {
  stopVideoProgressWatchdog()

  let lastTime = video.currentTime
  let stagnantChecks = 0

  videoProgressWatchdog = setInterval(() => {
    if (currentSlide !== index || !video || video.paused || video.ended) {
      stagnantChecks = 0
      lastTime = video ? video.currentTime : 0
      return
    }

    const now = video.currentTime
    const progressed = now > lastTime + 0.08

    if (progressed) {
      stagnantChecks = 0
      lastTime = now
      hideVideoLoading(index)
      return
    }

    stagnantChecks += 1

    // If the video has not advanced for ~1 second while it is supposed to
    // be playing, treat it as buffering even if Safari did not emit waiting.
    if (stagnantChecks >= 2)
      showVideoLoading(index)
  }, 500)
}

async function waitForBufferedAhead(video, index, minSeconds) {
  if (!isMobileVideoMode)
    return

  const startedAt = Date.now()

  while (currentSlide === index && !video.ended) {
    if (getBufferedAhead(video) >= minSeconds)
      return

    showVideoLoading(index)

    // Do not block forever on very slow networks. After a few seconds let
    // Safari try playback and let waiting/stalled events take over.
    if (Date.now() - startedAt > 6000)
      return

    await new Promise(resolve => setTimeout(resolve, 250))
  }
}

function getVideoLoadingIndicator() {
  return document.getElementById('video-global-loading')
}

function syncVideoLoaderToSite() {
  const loader = getVideoLoadingIndicator()
  const site = document.getElementById('fullpage')

  if (!loader || !site)
    return

  // Sidr desplaza la web al abrir el menú, pero un elemento position:fixed
  // puede quedarse anclado al viewport. Tomamos el desplazamiento real del
  // contenedor principal para que los tres puntos viajen exactamente con la web.
  const siteLeft = site.getBoundingClientRect().left
  loader.style.transform = `translate3d(${Math.round(siteLeft)}px, 0, 0)`
}

function startVideoLoaderPositionSync() {
  if (videoLoaderPositionRaf)
    return

  const tick = () => {
    const loader = getVideoLoadingIndicator()

    if (!loader || loader.style.display === 'none') {
      videoLoaderPositionRaf = null
      return
    }

    syncVideoLoaderToSite()
    videoLoaderPositionRaf = requestAnimationFrame(tick)
  }

  syncVideoLoaderToSite()
  videoLoaderPositionRaf = requestAnimationFrame(tick)
}

function stopVideoLoaderPositionSync() {
  if (videoLoaderPositionRaf) {
    cancelAnimationFrame(videoLoaderPositionRaf)
    videoLoaderPositionRaf = null
  }
}

function showVideoLoading(_index) {
  const loader = getVideoLoadingIndicator()

  if (!loader || !videoSectionActive)
    return

  syncVideoLoaderToSite()
  loader.style.display = 'block'
  loader.setAttribute('aria-hidden', 'false')
  startVideoLoaderPositionSync()
}

function hideVideoLoading(_index) {
  const loader = getVideoLoadingIndicator()

  if (!loader)
    return

  loader.style.display = 'none'
  loader.setAttribute('aria-hidden', 'true')
  loader.style.transform = ''
  stopVideoLoaderPositionSync()
}













function bindVideoLoadingState(video, index) {
  if (!video || video.dataset.loadingStateBound === 'true')
    return

  video.dataset.loadingStateBound = 'true'

  const showIfActive = () => {
    if (currentSlide === index)
      showVideoLoading(index)
  }

  const hideIfActuallyPlaying = () => {
    if (currentSlide !== index)
      return

    if (!video.paused && video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA)
      hideVideoLoading(index)
  }

  // Safari does not always emit the same sequence of events. Listen to all
  // relevant buffering signals and rely on the watchdog as a fallback.
  video.addEventListener('loadstart', showIfActive)
  video.addEventListener('waiting', showIfActive)
  video.addEventListener('stalled', showIfActive)
  video.addEventListener('seeking', showIfActive)
  video.addEventListener('suspend', () => {
    if (currentSlide === index && getBufferedAhead(video) < 0.5)
      showVideoLoading(index)
  })

  video.addEventListener('playing', () => {
    if (currentSlide === index) {
      hideVideoLoading(index)
      startVideoProgressWatchdog(video, index)
    }
  })

  video.addEventListener('canplay', hideIfActuallyPlaying)
  video.addEventListener('seeked', hideIfActuallyPlaying)

  video.addEventListener('pause', () => {
    if (currentSlide === index && !video.ended && video.dataset.autoAdvanceInProgress !== 'true')
      showVideoLoading(index)
  })

  video.addEventListener('ended', () => {
    if (currentSlide === index)
      hideVideoLoading(index)
  })

  video.addEventListener('error', () => {
    if (currentSlide === index)
      hideVideoLoading(index)
  })
}

function ensureVideoSource(video, index) {
  if (!video)
    return false

  bindVideoLoadingState(video, index)

  const source = video.dataset.source
  if (!source)
    return false

  const hasSource = !!video.getAttribute('src')

  if (!hasSource) {
    video.src = source
    video.preload = 'auto'

    if (!loadedVideoIndexes.includes(index))
      loadedVideoIndexes.push(index)

    video.load()
  }
  else if (!loadedVideoIndexes.includes(index)) {
    loadedVideoIndexes.push(index)
  }

  return true
}

function waitForVideoEvent(video, eventName, readyTest) {
  return new Promise((resolve, reject) => {
    if (readyTest())
      return resolve()

    const onReady = () => {
      cleanup()
      resolve()
    }

    const onError = () => {
      cleanup()
      reject(video.error || new Error(`Video error while waiting for ${eventName}`))
    }

    const cleanup = () => {
      video.removeEventListener(eventName, onReady)
      video.removeEventListener('error', onError)
    }

    video.addEventListener(eventName, onReady, { once: true })
    video.addEventListener('error', onError, { once: true })
  })
}

async function prepareVideoAtStart(video, index) {
  if (!ensureVideoSource(video, index))
    return false

  video.autoplay = false
  video.removeAttribute('autoplay')
  video.muted = userMuted
  video.playsInline = true
  video.pause()

  try {
    await waitForVideoEvent(
      video,
      'loadedmetadata',
      () => video.readyState >= HTMLMediaElement.HAVE_METADATA
    )

    // Reset only after metadata is available. On some mobile browsers assigning
    // currentTime before this point is ignored or throws.
    if (Math.abs(video.currentTime) > 0.05)
      video.currentTime = 0
    else
      video.currentTime = 0

    await waitForVideoEvent(
      video,
      'loadeddata',
      () => video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA
    )

    await waitForVideoEvent(
      video,
      'canplay',
      () => video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA
    )

    return true
  }
  catch (error) {
    console.warn('3VE: no se pudo preparar el vídeo', index, error)
    return false
  }
}

async function startVideoSafely(video, index, restartFromBeginning = true) {
  if (!video)
    return

  cancelVideoPreloadTimer()
  stopAllOtherVideoLoads(index)
  showVideoLoading(index)

  let ready = true

  if (restartFromBeginning) {
    delete savedVideoTimes[index]
    ready = await prepareVideoAtStart(video, index)
  }
  else {
    ensureVideoSource(video, index)
    video.muted = userMuted
  }

  // The user may have changed slides while this video was buffering.
  if (!ready || currentSlide !== index)
    return

  try {
    await waitForBufferedAhead(video, index, MOBILE_MIN_BUFFER_AHEAD)

    if (currentSlide !== index)
      return

    await video.play()
    hideVideoLoading(index)
    startVideoProgressWatchdog(video, index)

    if (!startedVideoIndexes.includes(index))
      startedVideoIndexes.push(index)

    scheduleNextVideoPreload(index, video)
  }
  catch (error) {
    hideVideoLoading(index)
    console.warn('3VE: reproducción bloqueada o no disponible', index, error)
  }
}

async function resumeVideoSafely(video, index) {
  if (!video || currentSlide !== index)
    return

  cancelVideoPreloadTimer()
  stopAllOtherVideoLoads(index)

  const hasSource = ensureVideoSource(video, index)
  if (!hasSource)
    return

  video.muted = userMuted

  if (video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA)
    showVideoLoading(index)

  try {
    await waitForVideoEvent(
      video,
      'loadedmetadata',
      () => video.readyState >= HTMLMediaElement.HAVE_METADATA
    )

    const savedTime = savedVideoTimes[index]
    if (Number.isFinite(savedTime) && savedTime > 0) {
      // Restore the exact point where this slide was left.
      // If the source had to be reattached, seeking after loadedmetadata avoids
      // Safari/iOS ignoring currentTime assignments made too early.
      if (Math.abs(video.currentTime - savedTime) > 0.15)
        video.currentTime = savedTime
    }

    await waitForVideoEvent(
      video,
      'canplay',
      () => video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA
    )

    if (currentSlide !== index)
      return

    await waitForBufferedAhead(video, index, MOBILE_MIN_BUFFER_AHEAD)

    if (currentSlide !== index)
      return

    await video.play()
    hideVideoLoading(index)
    startVideoProgressWatchdog(video, index)

    if (!startedVideoIndexes.includes(index))
      startedVideoIndexes.push(index)

    scheduleNextVideoPreload(index, video)
  }
  catch (error) {
    hideVideoLoading(index)
    console.warn('3VE: no se pudo reanudar el vídeo', index, error)
  }
}

function unloadVideoSource(video, index) {
  if (!video)
    return

  // Preserve the logical playback position before releasing the network resource.
  if (Number.isFinite(video.currentTime) && video.currentTime > 0)
    savedVideoTimes[index] = video.currentTime

  video.pause()
  video.preload = 'none'

  // Removing src + load() aborts outstanding Range requests in browsers,
  // releasing bandwidth and decoder resources for the active slide.
  if (video.getAttribute('src')) {
    video.removeAttribute('src')
    video.load()
  }

  const loadedPos = loadedVideoIndexes.indexOf(index)
  if (loadedPos !== -1)
    loadedVideoIndexes.splice(loadedPos, 1)

  hideVideoLoading(index)
}

function stopAllOtherVideoLoads(activeIndex) {
  document.querySelectorAll('video[id^="video-"]').forEach(video => {
    const match = video.id.match(/^video-(\d+)$/)
    if (!match)
      return

    const index = Number(match[1])

    if (index !== activeIndex)
      unloadVideoSource(video, index)
  })
}

function cancelVideoPreloadTimer() {
  if (videoPreloadTimer) {
    clearTimeout(videoPreloadTimer)
    videoPreloadTimer = null
  }
}

function scheduleNextVideoPreload(index, currentVideo) {
  cancelVideoPreloadTimer()

  // On mobile the active video gets absolute network priority.
  // Do not start a second MP4 request in the background.
  if (isMobileVideoMode)
    return

  const checkStablePlayback = () => {
    // Do nothing if the visitor has already moved to another slide.
    if (currentSlide !== index || currentVideo.paused || currentVideo.ended)
      return

    // HAVE_FUTURE_DATA means the browser currently has enough decoded/buffered
    // information to keep moving. Only then do we spend bandwidth on the next video.
    if (currentVideo.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA &&
        currentVideo.currentTime >= 2.5) {
      preloadNextVideo(index)
      videoPreloadTimer = null
      return
    }

    // If the connection is struggling, give the current video priority and retry later.
    videoPreloadTimer = setTimeout(checkStablePlayback, 1500)
  }

  // Give the active video roughly three seconds of exclusive bandwidth first.
  videoPreloadTimer = setTimeout(checkStablePlayback, 3000)
}

function preloadNextVideo(currentIndex) {
  if (isMobileVideoMode)
    return
  const slides = Array.from(document.querySelectorAll('.horizontal-scrolling'))

  for (let i = currentIndex + 1; i < slides.length; i++) {
    if (slides[i].dataset.video !== 'true')
      continue

    const nextVideo = document.getElementById(`video-${i}`)
    if (!nextVideo || !nextVideo.dataset.source)
      return

    // Keep network activity limited to: current video + exactly one next video.
    document.querySelectorAll('video[id^="video-"]').forEach(video => {
      const match = video.id.match(/^video-(\d+)$/)
      if (!match)
        return

      const videoIndex = Number(match[1])
      if (videoIndex !== currentIndex && videoIndex !== i)
        unloadVideoSource(video, videoIndex)
    })

    if (!nextVideo.getAttribute('src')) {
      nextVideo.muted = true
      nextVideo.playsInline = true
      nextVideo.preload = 'auto'
      nextVideo.src = nextVideo.dataset.source

      if (!loadedVideoIndexes.includes(i))
        loadedVideoIndexes.push(i)

      // preload="auto" is only a hint on mobile browsers, but load() gives the
      // browser the opportunity to start a Range request without playing it.
      nextVideo.load()
    }

    // Exactly one future video is considered.
    return
  }
}

function bindVideoAutoAdvance(video, index, page) {
  if (!video || !['_3d_animations', 'virtual_reality'].includes(page))
    return

  video.loop = false
  video.removeAttribute('loop')

  if (video.dataset.autoAdvanceBound === 'true')
    return

  video.dataset.autoAdvanceBound = 'true'

  const advance = () => {
    if (currentSlide !== index || video.dataset.autoAdvanceTriggered === 'true')
      return

    video.dataset.autoAdvanceTriggered = 'true'
    video.dataset.autoAdvanceInProgress = 'true'

    stopVideoProgressWatchdog()
    hideVideoLoading(index)
    delete savedVideoTimes[index]

    try {
      video.pause()
      video.currentTime = 0
    }
    catch (_error) {}

    // La instancia sólo es fiable DESPUÉS de terminar el constructor de fullPage.
    // El primer vídeo se enlazaba durante afterRender y capturaba undefined.
    // Usamos siempre la API global ya inicializada, igual que validamos en consola.
    requestAnimationFrame(() => {
      const api = window.fullpage_api
      if (api && typeof api.moveSlideRight === 'function')
        api.moveSlideRight()
    })
  }

  video.addEventListener('playing', () => {
    if (video.currentTime < 0.5) {
      video.dataset.autoAdvanceTriggered = 'false'
      delete video.dataset.autoAdvanceInProgress
    }
  })

  video.addEventListener('ended', advance)

  // Fallback: Firefox/Safari pueden dejar el primer vídeo detenido en el último
  // fotograma sin entregar ended de forma consistente. Si estamos realmente al
  // final, avanzamos una sola vez por la misma ruta.
  video.addEventListener('timeupdate', () => {
    if (!Number.isFinite(video.duration) || video.duration <= 0)
      return

    if (video.duration - video.currentTime <= 0.12)
      advance()
  })
}

function bindAllVideoAutoAdvance(page) {
  if (!['_3d_animations', 'virtual_reality'].includes(page))
    return

  document.querySelectorAll('.horizontal-scrolling[data-video="true"]').forEach(slide => {
    const match = slide.id.match(/^slide-index-(\d+)$/)
    if (!match)
      return

    const index = Number(match[1])
    const video = document.getElementById(`video-${index}`)
    bindVideoAutoAdvance(video, index, page)
  })
}

function getHomeSeenIds() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(HOME_SEEN_STORAGE_KEY) || '[]')
    return new Set(Array.isArray(parsed) ? parsed.map(Number) : [])
  }
  catch (_error) {
    return new Set()
  }
}

function saveHomeSeenIds(seen) {
  try {
    sessionStorage.setItem(HOME_SEEN_STORAGE_KEY, JSON.stringify(Array.from(seen)))
  }
  catch (_error) {}
}

function markHomeCarouselItemSeen(item) {
  if (!item || item.id === undefined || item.id === null)
    return

  const seen = getHomeSeenIds()
  seen.add(Number(item.id))
  saveHomeSeenIds(seen)
}

function getHomeCarouselItems() {
  const dataNode = document.getElementById('home-hero-items-data')
  if (!dataNode)
    return []

  try {
    const parsed = JSON.parse(dataNode.textContent || '[]')
    if (!Array.isArray(parsed))
      return []

    // No permitimos duplicados del mismo ProjectItem dentro de un ciclo.
    const unique = []
    const ids = new Set()
    parsed.forEach(item => {
      const id = Number(item?.id)
      if (!Number.isFinite(id) || ids.has(id))
        return
      ids.add(id)
      unique.push(item)
    })

    // Persistimos lo visto durante esta sesión del navegador. De este modo,
    // incluso al recargar HOME, no reaparece un item hasta agotar todos los
    // elementos marcados como portada. Cuando ya se han visto todos, comienza
    // un ciclo nuevo completo.
    const seen = getHomeSeenIds()
    let unseen = unique.filter(item => !seen.has(Number(item.id)))

    if (!unseen.length && unique.length) {
      try { sessionStorage.removeItem(HOME_SEEN_STORAGE_KEY) } catch (_error) {}
      unseen = unique
    }

    return unseen
  }
  catch (error) {
    console.warn('3VE: no se pudo leer el carrusel HOME', error)
    return []
  }
}

function getHomeImageUrl(item) {
  if (!item || !item.id || !item.image_filename)
    return null

  const screenWidth = Math.min(5120, window.innerWidth * window.devicePixelRatio)
  const screenHeight = Math.min(5120, window.innerHeight * window.devicePixelRatio)
  const widths = [720,1024,1280,1440,1600,1920,2048,2160,2560,2880,3200,3840,4096,5120]
  const target = screenWidth > screenHeight ? screenWidth : screenHeight
  const imageWidth = widths.find(width => width >= target) || 5120

  return `/uploads/project_item/image1/${item.id}/img${imageWidth}_${item.image_filename}`
}

function getHomeImageLayers() {
  return [
    document.getElementById('home-hero-image-layer-0'),
    document.getElementById('home-hero-image-layer-1')
  ].filter(Boolean)
}

function getHomeImageLayer() {
  const layers = getHomeImageLayers()
  return layers[homeActiveImageLayerIndex] || layers[0] || null
}

function hideAllHomeImageLayers() {
  getHomeImageLayers().forEach(layer => layer.classList.remove('is-visible'))
}

function getHomeIframe() {
  return document.getElementById('home-hero-iframe')
}

function getOrCreateHomeVideo() {
  let video = document.getElementById('video-0')

  if (!video) {
    video = document.createElement('video')
    video.id = 'video-0'
    video.preload = 'none'
    video.muted = true
    video.playsInline = true
    video.setAttribute('playsinline', '')
  }

  video.loop = false
  video.removeAttribute('loop')
  video.classList.add('home-viewport-video')

  // Clave del arreglo validado en consola: el vídeo HOME queda fuera del
  // contexto de fullPage y se referencia directamente al viewport.
  if (video.parentElement !== document.body)
    document.body.appendChild(video)

  return video
}

function hideHomeVolume() {
  const volume = document.getElementById('volume')
  if (!volume)
    return

  removeVolumeListener()
  volume.style.display = 'none'
}

function updateHomeContainerMediaFlags(item) {
  const container = document.getElementById('container-first-image')
  if (!container || !item)
    return

  container.dataset.id = item.id || ''
  container.dataset.video = item.type === 'video' ? 'true' : 'false'
  container.dataset.iframe = item.type === 'iframe' ? 'true' : 'false'
  container.dataset.image = item.type === 'image' ? (item.image_filename || 'home-image') : ''
}

function resetHomeVideoForNewItem(video, item) {
  if (!video)
    return

  stopVideoProgressWatchdog()
  cancelVideoPreloadTimer()

  if (homeCarouselVideoEndedHandler) {
    video.removeEventListener('ended', homeCarouselVideoEndedHandler)
    homeCarouselVideoEndedHandler = null
  }

  video.pause()
  video.classList.remove('is-visible')

  if (video.getAttribute('src')) {
    video.removeAttribute('src')
    video.load()
  }

  const loadedPos = loadedVideoIndexes.indexOf(0)
  if (loadedPos !== -1)
    loadedVideoIndexes.splice(loadedPos, 1)

  const startedPos = startedVideoIndexes.indexOf(0)
  if (startedPos !== -1)
    startedVideoIndexes.splice(startedPos, 1)

  delete savedVideoTimes[0]

  video.dataset.source = item.video_url || ''
  video.dataset.color = item.color || ''
  video.loop = false
  video.removeAttribute('loop')
}

function hideHomeIframe() {
  const iframe = getHomeIframe()
  if (!iframe)
    return

  iframe.style.opacity = '0'
  iframe.style.visibility = 'hidden'
  iframe.style.pointerEvents = 'none'
}

function showHomeIframeElement(iframe) {
  iframe.style.opacity = '1'
  iframe.style.visibility = 'visible'
  iframe.style.pointerEvents = 'auto'
}

function ensureHomeIframe() {
  let iframe = getHomeIframe()

  if (!iframe) {
    iframe = document.createElement('iframe')
    iframe.id = 'home-hero-iframe'
    iframe.className = 'tour-interactive-frame'
    iframe.setAttribute('allowfullscreen', '')
    iframe.setAttribute('allow', 'autoplay; fullscreen; accelerometer; gyroscope; xr-spatial-tracking')
    iframe.style.opacity = '0'
    iframe.style.visibility = 'hidden'
    iframe.style.pointerEvents = 'none'
    iframe.style.transition = `opacity ${HOME_MEDIA_FADE_MS}ms ease`

    const container = document.getElementById('container-first-image')
    container?.appendChild(iframe)
  }

  return iframe
}

function clearHomeCarouselTimer() {
  if (homeCarouselTimer) {
    clearTimeout(homeCarouselTimer)
    homeCarouselTimer = null
  }
}

function preloadNextHomeImage() {
  if (!homeCarouselItems.length)
    return

  const nextIndex = (homeCarouselIndex + 1) % homeCarouselItems.length
  const nextItem = homeCarouselItems[nextIndex]

  if (!nextItem || nextItem.type !== 'image')
    return

  const url = getHomeImageUrl(nextItem)
  if (!url)
    return

  const image = new Image()
  image.src = url
}

function advanceHomeCarousel() {
  if (homeCarouselPaused || !homeCarouselItems.length)
    return

  const lastItemId = Number(homeCarouselItems[homeCarouselIndex]?.id)
  let nextIndex = homeCarouselIndex + 1

  // Al completar el bloque de no-vistos volvemos a consultar el catálogo.
  // Si ya se ha visto TODO en esta sesión, getHomeCarouselItems reinicia el
  // ciclo completo; así no repetimos un subconjunto antes de tiempo.
  if (nextIndex >= homeCarouselItems.length) {
    homeCarouselItems = getHomeCarouselItems()
    nextIndex = 0

    // En el límite entre ciclos evitamos repetir inmediatamente el mismo item.
    if (homeCarouselItems.length > 1 && Number(homeCarouselItems[0]?.id) === lastItemId)
      homeCarouselItems.push(homeCarouselItems.shift())
  }

  if (!homeCarouselItems.length)
    return

  if (homeCarouselItems.length === 1 && Number(homeCarouselItems[0]?.id) === lastItemId)
    return

  showHomeCarouselItem(nextIndex)
}

async function showHomeCarouselImage(item, token) {
  const layers = getHomeImageLayers()
  if (!layers.length)
    return

  const url = getHomeImageUrl(item)
  if (!url)
    return

  const image = new Image()

  await new Promise((resolve, reject) => {
    image.onload = resolve
    image.onerror = reject
    image.src = url
  }).catch(() => {})

  if (token !== homeCarouselToken || homeCarouselPaused)
    return

  const currentLayer = layers.find(layer => layer.classList.contains('is-visible')) || null
  const nextIndex = currentLayer ? (currentLayer === layers[0] ? 1 : 0) : homeActiveImageLayerIndex
  const nextLayer = layers[nextIndex] || layers[0]
  if (!nextLayer)
    return

  homeActiveImageLayerIndex = layers.indexOf(nextLayer)

  nextLayer.style.backgroundImage = `url("${url.replace(/"/g, '\\"')}")`
  nextLayer.classList.remove('is-visible')

  const video = document.getElementById('video-0')
  hideHomeIframe()
  hideHomeVolume()
  videoSectionActive = false
  hideVideoLoading(0)
  enableScroll()

  // Crossfade real: 1,4 s de fundido simultáneo entre la imagen saliente y la entrante.
  // La permanencia de cada imagen sigue siendo de 8 s. No hay corte a negro.
  requestAnimationFrame(() => {
    nextLayer.classList.add('is-visible')
    currentLayer?.classList.remove('is-visible')
    video?.classList.remove('is-visible')
  })

  markHomeCarouselItemSeen(item)

  if (video) video.pause()

  preloadNextHomeImage()

  clearHomeCarouselTimer()
  homeCarouselTimer = setTimeout(advanceHomeCarousel, HOME_IMAGE_DURATION_MS)
}

async function showHomeCarouselVideo(item, token) {
  const video = getOrCreateHomeVideo()
  resetHomeVideoForNewItem(video, item)

  currentSlide = 0
  videoSectionActive = true
  disableScroll()
  showVideoLoading(0)

  homeCarouselVideoEndedHandler = () => {
    if (token !== homeCarouselToken || homeCarouselPaused)
      return

    stopVideoProgressWatchdog()
    hideVideoLoading(0)
    delete savedVideoTimes[0]

    try {
      video.pause()
      video.currentTime = 0
    }
    catch (_error) {}

    // Conserva el último fotograma hasta que el siguiente medio esté listo;
    // así evitamos un corte negro entre items.
    setTimeout(advanceHomeCarousel, 80)
  }

  video.addEventListener('ended', homeCarouselVideoEndedHandler)

  await startVideoSafely(video, 0, true)

  if (token !== homeCarouselToken || homeCarouselPaused)
    return

  hideAllHomeImageLayers()
  hideHomeIframe()
  video.classList.add('is-visible')
  markHomeCarouselItemSeen(item)

  removeVolumeListener()
  showVolume(0, video)
  preloadNextHomeImage()
}

function showHomeCarouselTour(item, token) {
  const iframe = ensureHomeIframe()
  if (!iframe || !item?.iframe_url)
    return


  let revealed = false
  const reveal = () => {
    if (revealed || token !== homeCarouselToken || homeCarouselPaused)
      return

    revealed = true
    iframe.dataset.homeLoaded = 'true'

    hideAllHomeImageLayers()

    const video = document.getElementById('video-0')
    if (video) {
      video.pause()
      video.classList.remove('is-visible')
    }

    hideHomeVolume()
    videoSectionActive = false
    hideVideoLoading(0)
    disableScroll()
    showHomeIframeElement(iframe)
    markHomeCarouselItemSeen(item)

    // Un Tour detiene el carrusel: el usuario conserva el control hasta recargar.
    clearHomeCarouselTimer()
    initTourInteractionHints()
  }

  const wantedUrl = item.iframe_url
  const alreadyLoaded = iframe.dataset.homeLoaded === 'true' && iframe.dataset.homeUrl === wantedUrl

  if (alreadyLoaded) {
    reveal()
    return
  }

  iframe.dataset.homeLoaded = 'false'
  iframe.dataset.homeUrl = wantedUrl

  // El listener se registra ANTES de asignar src. En V9.12 podía ocurrir que
  // el iframe terminara de cargar antes de registrar el evento y quedase negro.
  iframe.addEventListener('load', reveal, { once: true })
  iframe.src = wantedUrl

  // Salvaguarda para navegadores/cachés que no entreguen el load de forma fiable.
  // Mostramos el iframe tras unos segundos en vez de dejar la HOME negra.
  setTimeout(reveal, 3500)
}

function showHomeCarouselItem(index) {
  if (!homeCarouselItems.length)
    return

  clearHomeCarouselTimer()
  homeCarouselToken += 1
  const token = homeCarouselToken

  homeCarouselIndex = ((index % homeCarouselItems.length) + homeCarouselItems.length) % homeCarouselItems.length
  const item = homeCarouselItems[homeCarouselIndex]
  homeCarouselCurrentType = item.type
  updateHomeContainerMediaFlags(item)

  if (item.type === 'video')
    showHomeCarouselVideo(item, token)
  else if (item.type === 'iframe')
    showHomeCarouselTour(item, token)
  else
    showHomeCarouselImage(item, token)
}

function initHomeHeroCarousel(fullPage) {
  if (homeCarouselInitialized)
    return

  homeCarouselItems = getHomeCarouselItems()
  if (!homeCarouselItems.length)
    return

  homeCarouselInitialized = true
  homeCarouselFullPage = fullPage
  homeCarouselIndex = 0
  homeCarouselPaused = false
  showHomeCarouselItem(0)
}

function pauseHomeHeroCarousel() {
  if (!homeCarouselInitialized)
    return

  homeCarouselPaused = true
  clearHomeCarouselTimer()
  stopVideoProgressWatchdog()

  const video = document.getElementById('video-0')
  if (homeCarouselCurrentType === 'video' && video) {
    if (Number.isFinite(video.currentTime) && video.currentTime > 0)
      savedVideoTimes[0] = video.currentTime
    video.pause()
  }

  setHomeHeroMediaVisible(false)
  hideHomeVolume()
}

function resumeHomeHeroCarousel() {
  if (!homeCarouselInitialized || !homeCarouselPaused)
    return

  homeCarouselPaused = false
  const item = homeCarouselItems[homeCarouselIndex]
  if (!item)
    return

  updateHomeContainerMediaFlags(item)

  if (item.type === 'video') {
    const video = getOrCreateHomeVideo()
    videoSectionActive = true
    disableScroll()
    video.classList.add('is-visible')
    currentSlide = 0
    resumeVideoSafely(video, 0)
    removeVolumeListener()
    showVolume(0, video)
  }
  else if (item.type === 'iframe') {
    const iframe = getHomeIframe()
    if (iframe) {
      disableScroll()
      showHomeIframeElement(iframe)
      initTourInteractionHints()
    }
  }
  else {
    enableScroll()
    getHomeImageLayer()?.classList.add('is-visible')
    clearHomeCarouselTimer()
    homeCarouselTimer = setTimeout(advanceHomeCarousel, HOME_IMAGE_DURATION_MS)
  }
}

function arrangeTagButtons() {
  const tagButtonContainers = document.querySelectorAll('.tag-btns-container-row-2')

  tagButtonContainers.forEach(container => {
    if (container.children.length === 4)
      container.classList.add('md-justify-center')
  })
}


function isGalleryIndexWithinWindow(index) {
  if (!gallerySlideCount || index === undefined || index === null)
    return true

  const rawDistance = Math.abs(index - galleryActiveIndex)
  const circularDistance = Math.min(rawDistance, gallerySlideCount - rawDistance)
  return circularDistance <= GALLERY_IMAGE_WINDOW
}

function removeLoadedImageIndex(index) {
  const position = loadedImageIndexes.indexOf(index)
  if (position >= 0)
    loadedImageIndexes.splice(position, 1)
}

function preloadGalleryImage(slide, index) {
  if (!slide || !slide.dataset.image)
    return

  if (loadedImageIndexes.includes(index) && slide.style.backgroundImage)
    return

  if (slide.dataset.galleryPreloadPending === 'true')
    return

  const beginLoad = imageUrl => {
    if (!imageUrl || slide.dataset.galleryPreloadPending === 'true')
      return

    slide.dataset.galleryPreloadPending = 'true'
    const img = new Image()

    img.onload = () => {
      delete slide.dataset.galleryPreloadPending

      if (!isGalleryIndexWithinWindow(index))
        return

      slide.style.backgroundImage = `url(${img.src})`
      slide.style.backgroundSize = 'cover'

      if (!loadedImageIndexes.includes(index))
        loadedImageIndexes.push(index)
    }

    img.onerror = () => {
      delete slide.dataset.galleryPreloadPending
    }

    img.src = imageUrl
  }

  if (slide.dataset.sizedImage) {
    beginLoad(slide.dataset.sizedImage)
    return
  }

  if (slide.dataset.galleryPreloadObserver === 'true')
    return

  slide.dataset.galleryPreloadObserver = 'true'

  const observer = new MutationObserver(() => {
    const imageUrl = slide.dataset.sizedImage
    if (!imageUrl)
      return

    observer.disconnect()
    delete slide.dataset.galleryPreloadObserver
    beginLoad(imageUrl)
  })

  observer.observe(slide, {
    attributes: true,
    attributeFilter: ['data-sized-image'],
  })
}

function updateProjectImageWindow(activeIndex) {
  const slides = Array.from(document.querySelectorAll('.horizontal-scrolling[id^="slide-index-"]'))
    .filter(slide => slide.dataset.image)

  if (!slides.length) {
    gallerySlideCount = 0
    return
  }

  const indexedSlides = slides
    .map(slide => {
      const match = slide.id.match(/^slide-index-(\d+)$/)
      return match ? { slide, index: Number(match[1]) } : null
    })
    .filter(Boolean)

  if (!indexedSlides.length) {
    gallerySlideCount = 0
    return
  }

  gallerySlideCount = Math.max(...indexedSlides.map(item => item.index)) + 1
  galleryActiveIndex = Number.isFinite(Number(activeIndex)) ? Number(activeIndex) : 0

  indexedSlides.forEach(({ slide, index }) => {
    const rawDistance = Math.abs(index - galleryActiveIndex)
    const circularDistance = Math.min(rawDistance, gallerySlideCount - rawDistance)
    const keepLoaded = circularDistance <= GALLERY_IMAGE_WINDOW

    if (keepLoaded) {
      slide.dataset.galleryWindowActive = 'true'
      preloadGalleryImage(slide, index)
      return
    }

    slide.dataset.galleryWindowActive = 'false'
    slide.style.backgroundImage = ''
    slide.style.opacity = ''
    slide.style.transition = ''
    removeLoadedImageIndex(index)
  })
}

function setupSidrScrollFocus(fullPage, useNativeInfoScroll) {
  if (sidrFocusCleanup) {
    sidrFocusCleanup()
    sidrFocusCleanup = null
  }

  const sidr = document.getElementById('sidr')
  if (!sidr || !fullPage)
    return

  const activeSectionUsesNativeScroll = () => {
    if (!useNativeInfoScroll)
      return false

    const activeSection =
      document.querySelector('#fullpage > .section.active') ||
      document.querySelector('.section.active')

    return !!(activeSection && activeSection.classList.contains('scroll'))
  }

  const enterMenu = () => {
    fullPage.setAllowScrolling(false, 'up,down')
  }

  const leaveMenu = () => {
    fullPage.setAllowScrolling(!activeSectionUsesNativeScroll(), 'up,down')
  }

  const keepWheelInsideMenu = event => {
    event.stopPropagation()
  }

  sidr.addEventListener('pointerenter', enterMenu)
  sidr.addEventListener('pointerleave', leaveMenu)
  sidr.addEventListener('wheel', keepWheelInsideMenu, { passive: true })

  sidrFocusCleanup = () => {
    sidr.removeEventListener('pointerenter', enterMenu)
    sidr.removeEventListener('pointerleave', leaveMenu)
    sidr.removeEventListener('wheel', keepWheelInsideMenu)
  }
}

function showImage(contentContainer, imageContainer, slideIndex) {
  const alreadyLoaded = loadedImageIndexes.includes(slideIndex)

  if (alreadyLoaded) {
    // Imagen ya disponible: el texto entra rápido, una vez asentado el cambio.
    showSlideInfo(slideIndex, 225)
  }
  else {
    const img = new Image()
    const imageUrl = imageContainer.dataset.sizedImage

    if (imageUrl)
      img.src = imageUrl
    else
      observeChangesToImageUrl(imageContainer, imageUrl => img.src = imageUrl)

    function onImageLoad() {
      if (gallerySlideCount > 0 && slideIndex !== undefined && slideIndex !== null && !isGalleryIndexWithinWindow(slideIndex))
        return

      imageContainer.style.backgroundImage = `url(${img.src})`
      imageContainer.style.backgroundSize = 'cover'
      imageContainer.style.opacity = 0

      requestAnimationFrame(() => {
        imageContainer.style.transition = 'opacity 0.6s ease'
        imageContainer.style.opacity = 1
      })

      loadedImageIndexes.push(slideIndex)

      // El texto aparece ligado a la carga real de la imagen, no a un
      // temporizador de 2 segundos iniciado antes de que la imagen estuviera lista.
      showSlideInfo(slideIndex, 250)
    }

    img.onload = onImageLoad
  }
}

const observeChangesToImageUrl = (imageContainer, action) => {
  const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'data-sized-image') {
        const imageUrl = imageContainer.dataset.sizedImage
        action(imageUrl)
      }
    }
  })

  observer.observe(imageContainer, {
    attributes: true,                 // listen for attribute changes
    attributeFilter: ['data-sized-image'],  // only watch this specific attribute
  })
}

function showVolume(index, video) {
  const volume = document.getElementById('volume')
  volume.style.display = 'grid'

  showVolumeIcon(volume, userMuted)

  volumeListener = () => {
    userMuted = !userMuted
    localStorage.setItem('userMuted', userMuted)

    video.muted = userMuted
    if (!userMuted) video.play().catch(() => {})

    showVolumeIcon(volume, userMuted)
  }

  volume.addEventListener('click', volumeListener)
}

function removeVolumeListener() {
  const volume = document.getElementById('volume')
  if (!volume || !volumeListener)
    return

  volume.removeEventListener('click', volumeListener)
  volumeListener = null
}

function showVolumeIcon(volume, muted) {
  const volumeIcon = volume.querySelector('i')

  if (muted) {
    volumeIcon.classList.add('fa-volume-xmark')
    volumeIcon.classList.remove('fa-volume-low')
  }
  else {
    volumeIcon.classList.add('fa-volume-low')
    volumeIcon.classList.remove('fa-volume-xmark')
  }
}

function hideSlideInfo(index) {
  if (index === undefined || index === null)
    return

  if (slideInfoTimers[index]) {
    clearTimeout(slideInfoTimers[index])
    delete slideInfoTimers[index]
  }

  const infoContainer = document.getElementById(`info-container-${index}`)
  if (infoContainer) {
    infoContainer.style.transitionDuration = '1s'
    infoContainer.classList.add("container-hidden")
  }
}



function setHomeHeroMediaVisible(visible) {
  const video = document.getElementById('video-0')
  const iframe = document.getElementById('home-hero-iframe')
  const imageLayer = getHomeImageLayer()

  if (!visible) {
    video?.classList.remove('is-visible')
    hideAllHomeImageLayers()
    hideHomeIframe()
    return
  }

  if (homeCarouselCurrentType === 'video')
    video?.classList.add('is-visible')
  else if (homeCarouselCurrentType === 'iframe' && iframe)
    showHomeIframeElement(iframe)
  else if (homeCarouselCurrentType === 'image')
    imageLayer?.classList.add('is-visible')
}


function ensureTourHintStyles() {
  if (document.getElementById('3ve-tour-drag-hint-style'))
    return

  const style = document.createElement('style')
  style.id = '3ve-tour-drag-hint-style'
  style.textContent = `
    .tour-drag-hint {
      position: fixed;
      left: 50vw;
      top: 50vh;
      z-index: 2147483000;
      width: clamp(54px, 4.2vw, 76px);
      height: clamp(54px, 4.2vw, 76px);
      transform: translate(-50%, -50%);
      pointer-events: none;
      opacity: 0;
      visibility: hidden;
      transition: opacity .28s ease;
      filter: drop-shadow(0 2px 7px rgba(0,0,0,.38));
    }

    .tour-drag-hint.is-visible {
      opacity: .82;
      visibility: visible;
    }

    .tour-drag-hint .tour-drag-hand {
      width: 100%;
      height: 100%;
      animation: tour-drag-travel 1.45s cubic-bezier(.45,0,.55,1) infinite;
      transform-origin: center;
    }

    .tour-drag-hint .tour-touch-ring {
      transform-origin: 29px 15px;
      animation: tour-touch-pulse 1.45s ease-in-out infinite;
    }

    @keyframes tour-drag-travel {
      0%, 100% { transform: translateX(-24px); }
      50% { transform: translateX(24px); }
    }

    @keyframes tour-touch-pulse {
      0%, 100% { opacity: .15; transform: scale(.72); }
      22%, 72% { opacity: .72; transform: scale(1); }
    }

    @media (max-width: 768px) {
      .tour-drag-hint {
        width: 54px;
        height: 54px;
      }

      @keyframes tour-drag-travel {
        0%, 100% { transform: translateX(-19px); }
        50% { transform: translateX(19px); }
      }
    }
  `
  document.head.appendChild(style)
}

function isIframeActuallyVisible(iframe) {
  const rect = iframe.getBoundingClientRect()
  const width = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0))
  const height = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0))
  return width > window.innerWidth * 0.35 && height > window.innerHeight * 0.35
}

function createTourDragHint() {
  const hint = document.createElement('div')
  hint.className = 'tour-drag-hint'
  hint.setAttribute('aria-hidden', 'true')

  // SVG propio, lineal y neutro. No depende de Font Awesome ni de recursos externos.
  hint.innerHTML = `
    <svg class="tour-drag-hand" viewBox="0 0 64 64" aria-hidden="true">
      <circle class="tour-touch-ring"
              cx="29" cy="15" r="8"
              fill="none"
              stroke="rgba(255,255,255,.9)"
              stroke-width="1.6"/>
      <path d="M29 36V14.8
               C29 12.7 30.6 11 32.7 11
               C34.8 11 36.4 12.7 36.4 14.8V28.5
               V23.2C36.4 21.2 38 19.6 40 19.6
               C42 19.6 43.6 21.2 43.6 23.2V29.4
               V25.8C43.6 23.9 45.1 22.4 47 22.4
               C48.9 22.4 50.4 23.9 50.4 25.8V34.2
               C50.4 43.6 45.1 51 36.6 51
               H32.8
               C26.6 51 22.7 47.6 20.1 43.2
               L14.6 34.2
               C13.5 32.4 14 30.1 15.7 29
               C17.4 27.9 19.7 28.3 20.9 29.9
               L29 39.4Z"
            fill="rgba(255,255,255,.12)"
            stroke="rgba(255,255,255,.96)"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"/>
    </svg>
  `

  document.body.appendChild(hint)
  return hint
}

function initTourInteractionHints() {
  ensureTourHintStyles()

  document.querySelectorAll('#fullpage iframe').forEach((iframe) => {
    if (iframe.dataset.tourHintBound === 'true')
      return

    iframe.dataset.tourHintBound = 'true'

    const hint = createTourDragHint()
    let interacted = false
    let showTimer = null
    let hideTimer = null
    let repeatTimer = null

    const clearTimers = () => {
      if (showTimer) clearTimeout(showTimer)
      if (hideTimer) clearTimeout(hideTimer)
      if (repeatTimer) clearInterval(repeatTimer)
      showTimer = hideTimer = repeatTimer = null
    }

    const hideHint = () => hint.classList.remove('is-visible')

    const markInteracted = () => {
      if (interacted)
        return

      interacted = true
      hideHint()
      clearTimers()
      iframe.dataset.tourInteracted = 'true'
    }

    const flashHint = () => {
      if (interacted || !isIframeActuallyVisible(iframe))
        return

      hint.classList.add('is-visible')
      if (hideTimer)
        clearTimeout(hideTimer)
      hideTimer = setTimeout(hideHint, 2200)
    }

    const scheduleHints = () => {
      if (interacted)
        return

      clearTimers()
      showTimer = setTimeout(flashHint, 2600)
      repeatTimer = setInterval(flashHint, 15000)
    }

    const bindSameOrigin = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document
        if (!doc)
          return

        doc.addEventListener('pointerdown', markInteracted, { passive: true, once: true })
        doc.addEventListener('touchstart', markInteracted, { passive: true, once: true })
        doc.addEventListener('mousedown', markInteracted, { passive: true, once: true })
      }
      catch (_error) {
        // Cross-origin: el foco del iframe actúa como señal de interacción.
      }
    }

    iframe.addEventListener('load', () => {
      bindSameOrigin()
      scheduleHints()
    })

    iframe.addEventListener('focus', markInteracted, { once: true })

    window.addEventListener('blur', () => {
      setTimeout(() => {
        if (document.activeElement === iframe)
          markInteracted()
      }, 0)
    })

    scheduleHints()
  })
}


function showSlideInfo(index, requestedDelay = 325) {
  const slideIndex = index === undefined || index === null ? 0 : index

  if (slideInfoTimers[slideIndex])
    clearTimeout(slideInfoTimers[slideIndex])

  // V9.1: la primera diapositiva usa exactamente el mismo flujo visual
  // que las siguientes. El texto entra con un pequeno retardo/fade y no
  // depende de que la imagen o el video hayan terminado de cargar.
  const delay = Math.min(requestedDelay, 450)

  slideInfoTimers[slideIndex] = setTimeout(() => {
    if (currentSlide !== slideIndex) {
      delete slideInfoTimers[slideIndex]
      return
    }

    const infoContainer = document.getElementById(`info-container-${slideIndex}`)
    if (infoContainer) {
      infoContainer.style.transitionDuration = '1s'
      infoContainer.classList.remove("container-hidden")
    }

    delete slideInfoTimers[slideIndex]
  }, delay)
}

function disableScroll() {
  document.body.style.overflow = 'hidden'
}

function enableScroll() {
  document.body.style.overflow = ''
}

function showInfoSection() {
  const isMobile = window.innerWidth < 768

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible')
        observer.unobserve(entry.target) // only animate once
      }
    })
  },
  { rootMargin: `0px 0px ${isMobile ? '-200px' : '-400px'} 0px`})  // bottom margin shrunk by 400px - show elements when they are 400px in the screen


  document.querySelectorAll('.reveal').forEach(reveal => observer.observe(reveal))
}

function showScrollHint() {
  const lastSection = document.querySelector('.info-section.last')
  if (!lastSection) return

  let hint = document.getElementById('scroll-hint')
  if (!hint) {
    hint = document.createElement('div')
    hint.id = 'scroll-hint'
    hint.innerHTML = '<i class="fa-solid fa-chevron-down"></i>'
    document.body.appendChild(hint)
  }

  scrollHintObserver?.disconnect()

  hint.classList.remove('scroll-hint-hidden')

  scrollHintObserver = new IntersectionObserver((entries) => {
    hint.classList.toggle('scroll-hint-hidden', entries[0].isIntersecting)
  }, { threshold: 0.5 })

  scrollHintObserver.observe(lastSection)
}

function hideScrollHint() {
  document.getElementById('scroll-hint')?.classList.add('scroll-hint-hidden')
  scrollHintObserver?.disconnect()
  scrollHintObserver = null
}





